import { useMemo, useCallback } from "react";
import { NetworkStatus, type ApolloClient } from "@apollo/client";
import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import type {
  AdvancedFilters,
  BaseEntity,
  EntityHooks,
  SerializedViewState,
  TableFilters,
} from "../../UniversalCRMTable/types";
import type { FilterContextValue } from "@/contexts/FilterContext";
import type { AdvancedFilterGroup, FilterValue } from "@/contexts/FilterContext/types";
import type { GroupingOptions } from "@/lib/utils/crm/grouping-strategies/types";
import type { PaginationModeContextValue } from "../contexts/types";
import type { PaginationStateObject } from "../contexts/types/DataStateTypes";

type EntityCreateMutation<T extends BaseEntity> = ReturnType<EntityHooks<T>["useCreate"]>;
type EntityUpdateMutation<T extends BaseEntity> = ReturnType<EntityHooks<T>["useUpdate"]>;
type EntityDeleteMutation<T extends BaseEntity> = ReturnType<EntityHooks<T>["useDelete"]>;

interface CurrentFiltersInput {
  filters: Record<string, FilterValue>;
  search?: string;
  advancedFilters: AdvancedFilterGroup[];
  filterOperator: "AND" | "OR";
}

interface GroupableFieldDescriptor {
  fieldId: string;
  label: string;
  type: string;
  groupingOptions?: GroupingOptions;
}

export interface EntityPageQueryData<T extends BaseEntity> {
  data: T[];
  totalCount: number;
  pageInfo?: {
    hasNextPage?: boolean;
    hasPreviousPage?: boolean;
    startCursor?: string | null;
    endCursor?: string | null;
  };
}

interface UseEntityPageMemoizedStateParams<T extends BaseEntity> {
  filterContext: FilterContextValue<T> | null | undefined;
  paginationModeState: PaginationModeContextValue;
  currentFilters: CurrentFiltersInput;
  stableNoop: () => void;
  stableNoopObj: () => Record<string, never>;
  activeViewId: string | null;
  setActiveViewId: (id: string | null) => void;
  applySavedView: (viewId: string | null) => void;
  setPagination: (update: PaginationStateObject) => void;
  filteredData: T[];
  ftsProcessedData: T[];
  infiniteScrollDisplayData: T[];
  isLoading: boolean;
  networkStatus: NetworkStatus | number | undefined;
  error: Error | null | undefined;
  refetch: () => void;
  data: EntityPageQueryData<T> | null | undefined;
  groupableFields: GroupableFieldDescriptor[];
  hasLoadedOnce: boolean;
  ftsIsLoading: boolean;
  ftsError: string | null;
  hasNextPage: boolean;
  loadMoreCursor: () => string | null | undefined;
  setCursor: (cursor: string | null) => void;
  setPage: (page: number) => void;
  createMutation: EntityCreateMutation<T>;
  updateMutation: EntityUpdateMutation<T>;
  deleteMutation: EntityDeleteMutation<T>;
  router: AppRouterInstance;
  searchParams: URLSearchParams | null;
  apolloClient: ApolloClient;
}

/**
 * Memoized state objects for UniversalEntityPage data hook.
 * Extracted to keep useUniversalEntityPageData under the 300-line limit.
 *
 * Bridges FilterContext's internal shape (simple: Record, advanced:
 * AdvancedFilterGroup[]) to the EntityPage filterState contract (tableFilters:
 * TableFilters, advancedFilters: AdvancedFilters). `setTableFilters` calls
 * `replaceSimpleFilters` (bulk replace) instead of `setSimpleFilter` (single
 * field), which was the prior `any`-typed bug.
 */
export function useEntityPageMemoizedState<T extends BaseEntity>({
  filterContext,
  paginationModeState,
  currentFilters,
  stableNoop,
  stableNoopObj,
  activeViewId,
  setActiveViewId,
  applySavedView,
  setPagination,
  filteredData,
  ftsProcessedData,
  infiniteScrollDisplayData,
  isLoading,
  networkStatus,
  error,
  refetch,
  data,
  groupableFields,
  hasLoadedOnce,
  ftsIsLoading,
  ftsError,
  hasNextPage,
  loadMoreCursor,
  setCursor,
  setPage,
  createMutation,
  updateMutation,
  deleteMutation,
  router,
  searchParams,
  apolloClient,
}: UseEntityPageMemoizedStateParams<T>) {
  const infiniteScrollActive = paginationModeState.paginationMode.mode === "infinite-scroll";
  const hasCursor = !!paginationModeState.paginationMode.cursor;

  const processedData = useMemo(() => {
    if (infiniteScrollActive || hasCursor) {
      return infiniteScrollDisplayData;
    }
    return ftsProcessedData;
  }, [infiniteScrollActive, hasCursor, ftsProcessedData, infiniteScrollDisplayData]);

  const isFetchingNextPage =
    (infiniteScrollActive && networkStatus === NetworkStatus.fetchMore) ||
    (!infiniteScrollActive && hasCursor && isLoading);

  const loadMore = useCallback(() => {
    const nextCursor = loadMoreCursor();
    if (nextCursor) {
      setCursor(nextCursor);
    }
  }, [loadMoreCursor, setCursor]);

  const navigation = useMemo(
    () => ({ router, searchParams, apolloClient, activeViewId }),
    [router, searchParams, apolloClient, activeViewId]
  );

  const replaceSimpleFilters = filterContext?.actions.replaceSimpleFilters;
  const setTableFilters = useCallback(
    (filters: TableFilters) => {
      if (replaceSimpleFilters) {
        replaceSimpleFilters(filters);
      }
    },
    [replaceSimpleFilters]
  );

  // setAdvancedFilters bridges the EntityPage contract (AdvancedFilters object
  // map) to FilterContext's array shape. The active filter path uses the array
  // directly through FilterContext, so this stub remains a no-op until a
  // converter is needed for a real caller. Currently no caller invokes this
  // setter — saved-view application goes through FilterContext.applyPreset.
  const setAdvancedFilters = useCallback(
    (_filters: AdvancedFilters) => {
      // intentional no-op
    },
    []
  );

  const filterState = useMemo(
    () => ({
      searchTerm: filterContext?.filters.search || "",
      setSearchTerm: filterContext?.actions.setSearchTerm || stableNoop,
      debouncedSearchTerm: filterContext?.filters.search || "",
      tableFilters: (filterContext?.filters.simple || {}) as TableFilters,
      setTableFilters,
      currentFilters,
      setCurrentFilters: stableNoop,
      advancedFilters: {},
      setAdvancedFilters,
      filterOperator: "AND" as const,
      setFilterOperator: stableNoop,
      serializeFilters: null as (() => SerializedViewState) | null,
      setSerializeFilters: stableNoop,
      applyFilters: null as
        | ((filters: SerializedViewState, isManualSelection?: boolean) => void)
        | null,
      setApplyFilters: stableNoop,
      activeViewId,
      setActiveViewId,
      applySavedView,
      convertToGraphQL: stableNoopObj,
      filterContext: filterContext ?? undefined,
    }),
    [
      filterContext,
      filterContext?.filters.search,
      filterContext?.filters.simple,
      filterContext?.actions.setSearchTerm,
      setTableFilters,
      setAdvancedFilters,
      currentFilters,
      stableNoop,
      stableNoopObj,
      activeViewId,
      setActiveViewId,
      applySavedView,
    ]
  );

  const paginationState = useMemo(
    () => ({
      currentPage: paginationModeState.paginationMode.page,
      setCurrentPage: setPage,
      pagination: paginationModeState.paginationMode,
      setPagination,
    }),
    [paginationModeState.paginationMode, setPage, setPagination]
  );

  const mutations = useMemo(
    () => ({ createMutation, updateMutation, deleteMutation }),
    [createMutation, updateMutation, deleteMutation]
  );

  const dataState = useMemo(
    () => ({
      filteredData,
      processedData,
      isLoading,
      error,
      refetch,
      data,
      groupableFields,
      hasLoadedOnce,
      ftsIsLoading,
      ftsError,
      hasNextPage,
      isFetchingNextPage,
      loadMore,
    }),
    [
      filteredData,
      processedData,
      isLoading,
      error,
      refetch,
      data,
      groupableFields,
      hasLoadedOnce,
      ftsIsLoading,
      ftsError,
      hasNextPage,
      isFetchingNextPage,
      loadMore,
    ]
  );

  return {
    processedData,
    isFetchingNextPage,
    loadMore,
    navigation,
    filterState,
    paginationState,
    mutations,
    dataState,
  };
}
